/* eslint-disable react-hooks/exhaustive-deps */
import { Chip, TextField, Tooltip } from "@mui/material";
import { Autocomplete } from '@mui/material';
import React, { FC, ReactNode, useCallback, useEffect } from "react";

type InputComboBoxListProperties = {
    id?: string;
    value?: any;
    options: any[];
    label?: string;
    textField: string;
    valueField: string;
    autoComplete?: boolean;
    error?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    limitTags?: number;
    helperText?: ReactNode;
    simpleTooltip?: boolean;
    chipColor?: "default" | "primary" | "secondary" | undefined;
    renderOptions?: (option: any) => ReactNode
    renderOptionsLabel?: (option: any) => string
    onChange: (value: any, index?: number) => void
    onBlur?: Function;
    onFocus?: Function;
    disableClearable?: boolean;
}

const InputComboBoxList: FC<InputComboBoxListProperties> = ({
    id,
    label,
    value,
    options,
    textField,
    disabled = false,
    autoComplete = false,
    error = false,
    simpleTooltip = false,
    chipColor,
    valueField,
    renderOptions,
    renderOptionsLabel,
    limitTags,
    multiple,
    onChange,
    helperText,
    onBlur,
    onFocus,
    disableClearable,
}) => {
    const [localValue, setLocalValue] = React.useState<any>();
    const [inputValue, setInputValue] = React.useState('');

    useEffect(() => {
        if (!value) {
            setLocalValue(null)
            setInputValue('')
        } else {
            setLocalValue(Array.isArray(value) ? value.map((v) => v[textField]).join(", ") : value);
            setInputValue(value ? renderOptionsLabel ? renderOptionsLabel(value) : value[textField] : "")
        }
    }, [value]);


    const renderTooltip = useCallback(() => {
        if (simpleTooltip) {
            return inputValue
        }
        if ((localValue && multiple )) {
            return localValue;
        }

        if (localValue && renderOptions ) {
            return renderOptions(localValue);
        }

        return "";
    }, [simpleTooltip, localValue, multiple, renderOptions])

    return (
        <>
            <Tooltip title={renderTooltip()} >
                <Autocomplete
                    multiple={multiple}
                    fullWidth
                    id={id}
                    value={value}
                    disabled={disabled}
                    options={options}
                    limitTags={limitTags}
                    autoComplete={autoComplete}
                    disableClearable={disableClearable}
                    getOptionLabel={(option) =>
                        option
                        ? renderOptionsLabel
                            ? renderOptionsLabel(option)
                            : option[textField]
                        : ''
                    }
                    isOptionEqualToValue={(option, value) =>
                        !!value && option[valueField] === value[valueField]
                    }
                    onChange={(_event, newValue) => {
                        if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                            setLocalValue(null);
                            setInputValue('');
                        }

                        if (multiple) {
                            onChange(newValue, -1);
                        } else {
                            const index =
                                newValue && newValue[valueField]
                                ? options.findIndex((o) => o[valueField] === newValue[valueField])
                                : -1;

                            onChange(newValue, index);
                        }
                    }}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {renderOptions
                                ? renderOptions(option)
                                : option[textField]}
                        </li>
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            style={{ margin: '0px 1px' }}
                            size="small"
                            color={chipColor}
                            label={option[textField]}
                        />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        label={label}
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                        value={inputValue}
                        helperText={helperText}
                        error={error}
                        />
                    )}
                    onFocus={(event) => onFocus?.(event)}
                    onBlur={(event) => onBlur?.(event)}
                />
            </Tooltip>
        </>
    );
}

export { InputComboBoxList }